import { gqlClient } from '@/api/graphql-client';
import pipelineKeys from '@/api/queries/pipelines/keyFactory';
import { graphql } from '@/gql';
import {
  CreateAutomationToTargetPipelineStageInput,
  CreateAutomationToTargetPipelineStageMutation,
  GetPipelineStageTargetingAutomationsQuery,
} from '@/gql/graphql';
import {
  InfiniteData,
  UseMutationOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { DRAFT_MUTATION_SCOPE_ID } from '../../context/DraftModeContext';
import draftMutationKeys from './keyFactory';
import {
  applyOptimisticCreateAutomation,
  applyOptimisticDeleteAutomation,
  createEmptyTargetingAutomationsInfiniteData,
} from './optimisticUpdates';

const CREATE_AUTOMATION_TO_TARGET_PIPELINE_STAGE_MUTATION = graphql(`
  mutation CreateAutomationToTargetPipelineStage(
    $input: CreateAutomationToTargetPipelineStageInput!
  ) {
    createAutomationToTargetPipelineStage(input: $input) {
      ...PipelineTargetingAutomationCard
    }
  }
`);

type UseDraftCreateAutomationArgs = {
  pipelinePublicId: string;
  pipelineStagePublicId: string;
  pipelineTitle?: string;
  stageTitle?: string;
  options?: UseMutationOptions<
    CreateAutomationToTargetPipelineStageMutation,
    unknown,
    CreateAutomationToTargetPipelineStageInput
  >;
};

type MutationContext = {
  optimisticId: string;
};

const useDraftCreateAutomation = (args: UseDraftCreateAutomationArgs) => {
  const {
    pipelinePublicId,
    pipelineStagePublicId,
    pipelineTitle,
    stageTitle,
    options,
  } = args;
  const queryClient = useQueryClient();

  return useMutation<
    CreateAutomationToTargetPipelineStageMutation,
    unknown,
    CreateAutomationToTargetPipelineStageInput,
    MutationContext
  >({
    ...options,
    mutationKey: draftMutationKeys.createAutomation,
    mutationFn: (input: CreateAutomationToTargetPipelineStageInput) => {
      return gqlClient.request(
        CREATE_AUTOMATION_TO_TARGET_PIPELINE_STAGE_MUTATION,
        { input }
      );
    },
    scope: { id: DRAFT_MUTATION_SCOPE_ID },
    onMutate: async (variables) => {
      const queryKey = pipelineKeys.pipelineStageTargetingAutomations(
        pipelinePublicId,
        variables.pipelineStageId
      );

      await queryClient.cancelQueries({ queryKey });

      const previousData =
        queryClient.getQueryData<
          InfiniteData<GetPipelineStageTargetingAutomationsQuery>
        >(queryKey);

      const optimisticId = 'optimistic-' + Date.now();

      // For newly-added stages the query may not have resolved yet (or returned
      // `pipelineStage: null` because the stage isn't on the server). Seed an
      // empty shell so the optimistic trigger still renders.
      const baseData =
        previousData ??
        createEmptyTargetingAutomationsInfiniteData(variables.pipelineStageId);

      queryClient.setQueryData<
        InfiniteData<GetPipelineStageTargetingAutomationsQuery>
      >(
        queryKey,
        applyOptimisticCreateAutomation(baseData, {
          variables,
          optimisticId,
          pipelineTitle: pipelineTitle ?? '',
          stageTitle: stageTitle ?? '',
        })
      );

      void options?.onMutate?.(variables);
      return { optimisticId };
    },
    onError: (err, variables, context) => {
      // Surgically remove just this mutation's optimistic item so sibling
      // optimistic triggers queued on the same stage survive the rollback.
      const queryKey = pipelineKeys.pipelineStageTargetingAutomations(
        pipelinePublicId,
        variables.pipelineStageId
      );
      const current =
        queryClient.getQueryData<
          InfiniteData<GetPipelineStageTargetingAutomationsQuery>
        >(queryKey);

      if (current && context?.optimisticId) {
        queryClient.setQueryData(
          queryKey,
          applyOptimisticDeleteAutomation(current, context.optimisticId)
        );
      }

      options?.onError?.(err, variables, context);
    },
  });
};

export default useDraftCreateAutomation;
